import React, { useState } from 'react';
import BrowserAlertStatus from '../components/BrowserAlertStatus';
import { MetaTags } from '../components';
import { Breadcrumbs } from '../components';
import './Tools.css';

const tools = [
  {
    id: 'visa-eligibility-quiz',
    title: 'Visa Eligibility Quiz',
    description: 'Find out if you meet the requirements for your Ireland visa application with our quick interactive quiz.',
    icon: '✅',
    color: '#10b981',
    link: '/tools/eligibility-quiz',
    features: ['Quick 2-minute assessment', 'Personalized recommendations', 'Identifies potential issues']
  },
  {
    id: 'document-checklist',
    title: 'Document Checklist Generator',
    description: 'Generate a customized document checklist based on your visa type and profile requirements.',
    icon: '📋',
    color: '#3b82f6',
    link: '/tools/document-checklist',
    features: ['Customized for your visa type', 'Downloadable PDF', 'Track your progress']
  },
  {
    id: 'visa-fee-calculator',
    title: 'Visa Fee Calculator',
    description: 'Estimate entry-visa and related application costs using clearly labelled planning assumptions.',
    icon: '💰',
    color: '#f59e0b',
    link: '/tools/fee-calculator',
    features: ['Entry-fee estimate', 'Multiple visa types', 'Separate service estimates']
  },
  {
    id: 'processing-time-estimator',
    title: 'Processing Time Estimator',
    description: 'View illustrative processing ranges and dated official queue snapshots with their office scope.',
    icon: '⏱️',
    color: '#8b5cf6',
    link: '/tools/processing-time',
    features: ['Planning ranges', 'Dated queue snapshots', 'Office scope explained']
  },
  {
    id: 'decision-calculator',
    title: 'Decision Calculator',
    description: 'Review a profile-readiness checklist with practical preparation guidance.',
    icon: '📊',
    color: '#ec4899',
    link: '/tools/decision-calculator',
    features: ['Checklist signal', 'Preparation guidance', 'Clear limitations']
  },
  {
    id: 'education-loan-emi-calculator',
    title: 'Education Loan EMI Calculator',
    description: 'Calculate your education loan EMI, compare bank rates, and plan pre-payments for Ireland studies.',
    icon: '🏦',
    color: '#10b981',
    link: '/tools/education-loan-emi-calculator',
    features: ['Bank rate comparison', 'Amortization schedule', 'Pre-payment savings']
  },
  {
    id: 'cost-of-living-planner',
    title: 'Cost of Living Planner',
    description: 'Plan your monthly budget for studying in Ireland with city-wise expense breakdowns and tips.',
    icon: '🏙️',
    color: '#3b82f6',
    link: '/tools/cost-of-living-planner',
    features: ['City comparison', 'Lifestyle presets', 'Money-saving tips']
  },
  {
    id: 'ireland-university-matcher',
    title: 'Ireland University Matcher',
    description: 'Find the perfect Irish university based on your academic profile, course preferences, and budget.',
    icon: '🎓',
    color: '#169B62',
    link: '/tools/ireland-university-matcher',
    features: ['7 Irish universities', 'Smart matching algorithm', 'Compare up to 3 universities']
  },
  {
    id: 'ireland-scholarship-finder',
    title: 'Ireland Scholarship Finder',
    description: 'Discover scholarships available for Indian students to study in Ireland with eligibility matching.',
    icon: '🏆',
    color: '#FF8833',
    link: '/tools/ireland-scholarship-finder',
    features: ['Opportunity filters', 'Provider links', 'Expired deadlines hidden']
  },
  {
    id: 'ireland-visa-interview-simulator',
    title: 'Ireland Visa Interview Simulator',
    description: 'Practice visa interview with AI-generated questions based on visa type, sample answers, and scoring.',
    icon: '🎤',
    color: '#ec4899',
    link: '/tools/ireland-visa-interview-simulator',
    features: ['50+ interview questions', 'Practice & Mock modes', 'Score tracking']
  },
  {
    id: 'ireland-stamp-visualizer',
    title: 'Ireland Stamp Transition Visualizer',
    description: 'Interactive flowchart showing visa stamp progression pathway from Student to Citizenship.',
    icon: '🇮🇪',
    color: '#169B62',
    link: '/tools/ireland-stamp-visualizer',
    features: ['Visual pathway diagram', 'Timeline calculator', 'Requirements for each transition']
  },
  {
    id: 'irish-salary-tax-calculator',
    title: 'Irish Salary Tax Calculator',
    description: 'Calculate your take-home salary after Irish taxes (PAYE, USC, PRSI) for job offers and work permits.',
    icon: '💶',
    color: '#169B62',
    link: '/tools/irish-salary-tax-calculator',
    features: ['PAYE, USC, PRSI breakdown', 'Critical Skills indicator', 'India comparison']
  },
  {
    id: 'ireland-accommodation-assistant',
    title: 'Ireland Accommodation Assistant',
    description: 'Find student accommodation with city guides, platform comparisons, price estimates, and scam protection.',
    icon: '🏠',
    color: '#3b82f6',
    link: '/tools/ireland-accommodation-assistant',
    features: ['4 major cities', 'Platform directory', 'Scam protection guide']
  }
];

const ToolsPage = ({ onUpgrade, hasPremiumAccess = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleToolClick = (e, toolLink) => {
    if (hasPremiumAccess) {
      e.preventDefault();
      try {
        window.history.pushState({}, '', toolLink);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (err) {
        window.location.href = toolLink;
      }
      return;
    }
    e.preventDefault();
    if (typeof onUpgrade === 'function') onUpgrade();
  };

  return (
    <div className="App tools-page">
      <MetaTags
        title="Ireland Visa Tools & Calculators - Premium Access"
        description="Premium Ireland visa tools and calculators for Indian applicants. Upgrade once to unlock instant alerts and all 13 premium tools."
        keywords="Ireland visa tools, visa calculator Ireland, eligibility quiz, document checklist, visa fee calculator, processing time estimator, decision calculator"
        ogImage="/images/og-image.webp"
      />

      <header className="site-header">
        <div className="site-header-inner">
          <a className="brand" href="/" onClick={() => setMenuOpen(false)}>
            <img src="/logo.svg" alt="Irish Visa Tracker logo" className="brand-mark" />
            <span>Track Your Ireland Visa Decision</span>
          </a>
          <a href="#main" className="skip-link">Skip to content</a>
          <button className="menu-toggle" aria-controls="primary-nav" aria-expanded={menuOpen} onClick={() => setMenuOpen(v => !v)}>
            <span className="sr-only">Toggle navigation</span>☰ Menu
          </button>
          <nav className={`nav ${menuOpen ? 'open' : ''}`} id="primary-nav" role="navigation" aria-label="Primary">
            <a href="/" onClick={() => setMenuOpen(false)}>Home</a>
            <a href="/resources" onClick={() => setMenuOpen(false)}>Resources</a>
            <a href="/tools" onClick={() => setMenuOpen(false)} className="active">Tools</a>
            <a href="/pricing" onClick={() => setMenuOpen(false)}>Pricing</a>
            <a href="/faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            <a href="/about" onClick={() => setMenuOpen(false)}>About</a>
            <a href="/contact" onClick={() => setMenuOpen(false)}>Contact</a>
          </nav>
        </div>
      </header>

      <main id="main">
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Tools' }]} />
        <section className="tools-hero">
          <div className="container">
            <h1>Ireland Visa <span className="highlight">Tools & Calculators</span></h1>
            <p className="tools-subtitle">
              Upgrade once to unlock instant alerts and all 13 premium tools for your Ireland visa journey.
            </p>
          </div>
        </section>

        <section className="container section tools-section">
          <BrowserAlertStatus
            paid={hasPremiumAccess}
            email={typeof window !== 'undefined' ? window.localStorage.getItem('userEmail') || '' : ''}
          />
          <div className="tools-grid">
            {tools.map((tool) => (
              <a
                key={tool.id}
                href={tool.link}
                className="tool-card"
                style={{ '--tool-color': tool.color }}
                onClick={(e) => handleToolClick(e, tool.link)}
              >
                <div className="tool-icon" style={{ backgroundColor: `${tool.color}15`, color: tool.color }}>
                  {tool.icon}
                </div>
                <div className="tool-content">
                  <h3 className="tool-title">{tool.title}</h3>
                  <p className="tool-description">{tool.description}</p>
                  <ul className="tool-features">
                    {tool.features.map((feature, index) => (
                      <li key={index}>✓ {feature}</li>
                    ))}
                  </ul>
                </div>
                <div className="tool-arrow">→</div>
              </a>
            ))}
          </div>
        </section>

        <section className="container section cta-section">
          <div className="cta-card">
            <h2>Ready to Track Your Visa?</h2>
            <p>Get instant email and web push notifications when your visa status changes, plus full access to all 13 premium tools.</p>
            {hasPremiumAccess ? (
              <p className="premium-active-note">Premium access active — all tools are unlocked.</p>
            ) : (
              <button className="btn-primary btn-shimmer" onClick={onUpgrade}>
                Upgrade for ₹349 – Alerts + 13 Tools →
              </button>
            )}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-inner">
          <div className="footer-disclaimer">
            <strong>Disclosure:</strong> This service is only for Indian citizens (From India) applying for Ireland Visas.
          </div>
          <div className="footer-top">
            <div className="brand">Track Your Ireland Visa Decision</div>
            <div className="links">
              <a href="/privacy">Privacy</a>
              <a href="/terms">Terms</a>
              <a href="/refund">Refund</a>
              <a href="/data-deletion">Data Deletion</a>
              <a href="/editorial-policy">Editorial Policy</a>
            </div>
          </div>
          <div className="footer-bottom">
            <div className="powered-by">
              Powered by <a href="https://selfdriven.careers" target="_blank" rel="noopener noreferrer">SelfDriven.careers</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolsPage;
